import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { fromJS } from 'immutable'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { fetchEmbed, patchEmbedSuccess, patchEmbed } from 'app/modules/embed/actions'
import { getEmbedConfig } from 'app/modules/embed/selectors'
import { PATCH_EMBED_ERROR, PATCH_EMBED_SUCCESS } from 'app/modules/embed/constants'
import { PremiumGate } from 'components/payments/Gates'
import { SectionItem } from '../Section'
import { makeDefaultFailedContainer } from 'components/container'
import { WidgetMode } from 'constants/EmbedConfigurationConstants'
import Segment from './Segment'
import Target from './Target'
import OnlineHours from './OnlineHours'
import BackButton from '../BackButton'
import TimezonePicker from './TimezonePicker'
import WidgetModeSelector from './WidgetModeSelector'
import PremiumOverlay from 'components/shared/PremiumOverlay'
import PageTitle from '../PageTitle'
import { patchOperations } from 'utils/PatchHelper'

import * as OrgActions from 'app/modules/org/actions'
import { WithTour, startTour as startSeparateTour } from '@driftt/drift-tour'
import TourData from './tourInfo'
import { fetchUserProperties, updateUserPropertiesTheRightWay } from 'app/modules/user/properties/actions'
import {
  propertiesLoaded as userPropertiesLoaded,
  properties as userProperties,
} from 'app/modules/user/properties/selectors'
import { isLoaded as isOrgDataLoaded, getDateCreatedAt, properties } from 'app/modules/org/selectors'

import style from './style.styl'

const HasSeenTargetingHours = 'onboardingHasSeenTargetingHours'

const DisabledAudienceAndTargeting = (
  <PremiumOverlay className={style.DisabledAudienceAndTargeting}>
    <SectionItem title="Targeting">
      Reach the right people at exactly the right time with targeting. Target by segment, URL, time on page,
      and more.
    </SectionItem>
  </PremiumOverlay>
)

class HoursAndTargeting extends Component {
  static contextTypes = {
    app: PropTypes.object.isRequired,
  }

  componentDidMount() {
    const { userPropertiesLoaded, isOrgDataLoaded, orgProperties, actions } = this.props
    actions.fetchEmbed()

    if (userPropertiesLoaded && isOrgDataLoaded && !this.userHasSeenTour() && this.orgIsNewEnoughForTour()) {
      this.startTourAndMarkComplete()
    }

    if (!orgProperties[HasSeenTargetingHours]) {
      this.setOrgHasSeenProperty()
    }
  }

  componentWillReceiveProps(nextProps) {
    if (_.isEqual(nextProps.embed, this.props.embed)) return

    const isPatchSuccess = nextProps.embedStatus === PATCH_EMBED_SUCCESS
    const isPatchError = nextProps.embedStatus === PATCH_EMBED_ERROR

    isPatchSuccess &&
      this.context.app.actions.NotificationActions.pushNotification({
        content: 'Your changes have been saved!',
      })

    isPatchError &&
      this.context.app.actions.NotificationActions.pushNotification({
        content: 'Something went wrong saving your changes.',
        variant: 'red',
      })
  }

  setOrgHasSeenProperty = () => {
    const patchedProperties = patchOperations({
      [HasSeenTargetingHours]: true,
    })

    this.props.actions.patchOrgProperties(patchedProperties)
  }

  orgIsNewEnoughForTour = () => {
    const { orgCreatedAt } = this.props
    const now = new Date()
    now.setMonth(now.getMonth() - 1)

    return orgCreatedAt > now
  }

  userHasSeenTour = () => {
    const { userProperties } = this.props

    return !!userProperties.get(`tour--${TourData.scope}`)
  }

  startTourAndMarkComplete = () => {
    const {
      actions: { updateUserProperties },
    } = this.props

    setTimeout(() => startSeparateTour('settings'), TourData.delay)
    updateUserProperties({ [`tour--${TourData.scope}`]: true })
  }

  updateEmbed = changes => {
    this.props.actions.patchEmbed(changes)
  }

  render() {
    if (!this.props.embed) return null

    const { segments, users, gates, orgSettings, embed, Tooltip } = this.props
    const isInAutoMode = embed.widgetMode === WidgetMode.AUTO

    return (
      <div className={style.HoursAndTargeting}>
        <BackButton title="Drift Widget Settings" url="/settings/widget" />

        <PageTitle
          title="Hours & Targeting"
          description="Customize when your Drift Widget displays to your customers and visitors"
        />

        <Tooltip tooltipId="status">
          <SectionItem title="Drift Widget Online / Offline Status" className="radios">
            <WidgetModeSelector embed={embed} updateEmbed={this.updateEmbed} />
          </SectionItem>
        </Tooltip>

        {isInAutoMode && (
          <OnlineHours
            orgName={orgSettings.orgName}
            initialOpenHours={fromJS(embed.theme.openHours)}
            initialAvailabilityBasedOnline={fromJS(embed.theme.availabilityBasedOnline)}
            updateEmbed={this.updateEmbed}
          />
        )}

        <Tooltip tooltipId="timezone">
          <SectionItem title="Timezone" className="timezone">
            <TimezonePicker orgSettings={orgSettings} />
          </SectionItem>
        </Tooltip>

        <PremiumGate
          requiredGates={['has_11_2016_targeting']}
          FallbackComponent={DisabledAudienceAndTargeting}
        >
          <SectionItem
            title="Targeting"
            description={
              <div>
                <span>The Drift Widget is available to everyone who visits your website.</span>
                <a href="https://help.drift.com/article/displaying-the-chat-widget/" target="_blank">
                  {' '}
                  Learn More
                </a>
              </div>
            }
            className="targeting"
          />

          <Tooltip tooltipId="audience">
            <SectionItem className="segment">
              <Segment segments={segments} users={users} gates={gates} orgSettings={orgSettings} />
            </SectionItem>
          </Tooltip>

          <Tooltip tooltipId="displaywhen">
            <SectionItem className="target">
              <Target orgSettings={orgSettings} />
            </SectionItem>
          </Tooltip>
        </PremiumGate>
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    embed: getEmbedConfig(state),
    embedStatus: state.get('embed').get('status'),
    userPropertiesLoaded: userPropertiesLoaded(state),
    userProperties: userProperties(state),
    isOrgDataLoaded: isOrgDataLoaded(state),
    orgCreatedAt: getDateCreatedAt(state),
    orgProperties: properties(state),
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        fetchEmbed,
        patchEmbedSuccess,
        patchEmbed,
        fetchUserProperties,
        updateUserProperties: updateUserPropertiesTheRightWay,
        ...OrgActions,
      },
      dispatch
    ),
  }
}

const ReduxHoursAndTargeting = connect(
  mapStateToProps,
  mapDispatchToProps
)(HoursAndTargeting)

const HoursAndTargetingContainer = makeDefaultFailedContainer(ReduxHoursAndTargeting, {
  listenTo: [
    'stores.AvailabilityStore',
    'stores.SegmentStore',
    'stores.UserStore',
    'stores.GateStore',
    'stores.OrgSettingsStore',
    'stores.DefaultEmbedStore',
  ],
  fetch: {
    segments() {
      return this.app.stores.SegmentStore.getOrFetchSegments()
    },
    users() {
      return this.app.stores.UserStore.getOrFetchUsersForCurrentUserOrg()
    },
    gates() {
      return this.app.stores.GateStore.getOrFetchGates()
    },
    orgSettings() {
      return this.app.stores.OrgSettingsStore.getOrgSettings()
    },
  },
})

export default WithTour(TourData)(HoursAndTargetingContainer)



// WEBPACK FOOTER //
// ./src/scripts/components/settings/ChatWidget/components/HoursAndTargeting/index.js